<template>
    <h4>Transaction Lock Date</h4>
    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="lock-transaction-date-switch"
        >
            Lock Transaction Date
        </label>
        <input
            v-model="lockTransactionDate"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="lock-transaction-date-switch"
            @click="updateLockTransaction"
        />
    </div>
    <div class="mb-2">
        <label
            for="closing-date"
            class="form-label"
        >
            Closing Date
        </label>
        <input
            type="date"
            class="form-control width-20-per"
            id="closing-date"
            :disabled="!lockTransactionDate"
            v-model="closingDate"
            @change="handleClosingDateChange"
        >
    </div>

    <h4>Enable Voucher Approval</h4>
    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="is-voucher-approval-enabled"
        >
            Enable Voucher Approval
        </label>
        <input
            v-model="isVoucherApprovalEnabled"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="is-voucher-approval-enabled"
            @click="updateVoucherApproval"
        />
    </div>

    <h4>Enable Purchase Approval</h4>
    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="is-purchase-approval-enabled"
        >
            Enable Purchase Approval
        </label>
        <input
            v-model="isPurchaseApprovalEnabled"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="is-purchase-approval-enabled"
            @click="updatePurchaseApproval"
        />
    </div>

    <h4>Enable Stock Adjustment Approval</h4>
    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="is-stock-adjustment-approval-enabled"
        >
            Enable Stock Adjustment Approval
        </label>
        <input
            v-model="isStockAdjustmentApprovalEnabled"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="is-stock-adjustment-approval-enabled"
            @click="updateStockAdjustmentApproval"
        />
    </div>

    <h4>Enable Purchase Return Approval</h4>
    <div class="form-check form-switch d-flex align-items-center gap-2 ps-0 mb-2">
        <label
            class="form-check-label"
            for="is-purchase-return-approval-enabled"
        >
            Enable Purchase Return Approval
        </label>
        <input
            v-model="isPurchaseReturnApprovalEnabled"
            class="form-check-input m-0"
            type="checkbox"
            role="switch"
            id="is-purchase-return-approval-enabled"
            @click="updatePurchaseReturnApproval"
        />
    </div>

    <h4>Financial Year</h4>
    <div class="row mb-2">
        <div class="col-4">
            <label class="form-label">Financial Year Start Month</label>
            <v-select
                placeholder="Select Start Month"
                v-model="financialYearStartMonth"
                :options="monthOptions"
                :reduce="item => item.value"
                label="name"
                @option:selected="handleFinancialYearStartMonthChange"
            />
        </div>
    </div>

    <GlobalLoader />
</template>

<script setup>
import {inject, onMounted, ref} from "vue";
    import handleCompany from "@/services/modules/company";
    import handleInventory from "@/services/modules/inventory";
    import GlobalLoader from "@/components/atom/GlobalLoader.vue";
    import {useStore} from "vuex";
    import {useRoute} from "vue-router";

    const lockTransactionDate = ref(false)
    const isVoucherApprovalEnabled = ref(false)
    const isPurchaseApprovalEnabled = ref(false)
    const isStockAdjustmentApprovalEnabled = ref(false)
    const isPurchaseReturnApprovalEnabled = ref(false)
    const closingDate = ref(null)
    const financialYearStartMonth = ref(null)
    const monthOptions = ref([])
    const { getCompanyPreferences, modifyCompanyPreference } = handleCompany();
    const { fetchHome } = handleInventory();
    const $store = useStore();
    const $route = useRoute();
    const showSuccess =  inject('showSuccess');
    const companyId = $route.params.companyId;

    const updateLockTransaction = (e) => {
        const nextValue = e.target.checked; // ✅ new value after click

        const data = {
            company_id: companyId,
            field: "lock_transaction_date",
            value: nextValue,
        };

        handleUpdate(data);
    };

    const updateVoucherApproval = (e) => {
        const nextValue = e.target.checked; // ✅ new value after click

        const data = {
            company_id: companyId,
            field: "is_voucher_approval_enabled",
            value: nextValue,
        };

        handleUpdate(data);
    };

    const updatePurchaseApproval = (e) => {
        const nextValue = e.target.checked;

        const data = {
            company_id: companyId,
            field: "is_purchase_approval_enabled",
            value: nextValue,
        };

        handleUpdate(data);
    };

    const updateStockAdjustmentApproval = (e) => {
        const nextValue = e.target.checked;

        const data = {
            company_id: companyId,
            field: "is_stock_adjustment_approval_enabled",
            value: nextValue,
        };

        handleUpdate(data);
    };

    const updatePurchaseReturnApproval = (e) => {
        const nextValue = e.target.checked;

        const data = {
            company_id: companyId,
            field: "is_purchase_return_approval_enabled",
            value: nextValue,
        };

        handleUpdate(data);
    };

    const handleFinancialYearStartMonthChange = (option) => {
        const data = {
            company_id: companyId,
            field: "financial_year_start_month",
            value: option.value,
        };
        handleUpdate(data);
    }

    const handleClosingDateChange = (e) => {
        const nextValue = e.target.value;

        const data = {
            company_id: companyId,
            field: "closing_date",
            value: nextValue,
        };

        handleUpdate(data);
    }

    const handleUpdate = (data) => {
        $store.state.loading = true;

        modifyCompanyPreference(data)
            .then((res) => {
                showSuccess(res.message);
            })
            .finally(() => ($store.state.loading = false));
    }

    const getInitialValue = (data, field) => {
        const item = data.find((item => item.field === field))

        return item ? item.value : null;
    }

    const parseBoolean = (v) => {
        if (typeof v === "boolean") return v;
        if (typeof v === "number") return v === 1;
        if (typeof v === "string") return v.trim() === "1";
        return false;
    }

    const getMonths = () => {
        return fetchHome().then(res => {
            if (!res.data) return;
            monthOptions.value = res.data.months;
        })
    }

    const getPreferences = () => {
        const queryObject = {
            company_id: companyId,
            fields: 'lock_transaction_date,closing_date,is_voucher_approval_enabled,is_purchase_approval_enabled,is_stock_adjustment_approval_enabled,is_purchase_return_approval_enabled,financial_year_start_month'
        }
        const query = '?' + new URLSearchParams(queryObject).toString();
        return getCompanyPreferences(query).then(res => {
            if (!res.data) return;
            const data = res.data;
            lockTransactionDate.value = parseBoolean(getInitialValue(data, 'lock_transaction_date'));
            isVoucherApprovalEnabled.value = parseBoolean(getInitialValue(data, 'is_voucher_approval_enabled'));
            isPurchaseApprovalEnabled.value = parseBoolean(getInitialValue(data, 'is_purchase_approval_enabled'));
            isStockAdjustmentApprovalEnabled.value = parseBoolean(getInitialValue(data, 'is_stock_adjustment_approval_enabled'));
            isPurchaseReturnApprovalEnabled.value = parseBoolean(getInitialValue(data, 'is_purchase_return_approval_enabled'));
            closingDate.value = getInitialValue(data, 'closing_date')
            financialYearStartMonth.value = getInitialValue(data, 'financial_year_start_month')
        })
    }

    onMounted(() => {
        $store.state.loading = true
        Promise.all([getMonths(), getPreferences()])
            .finally(() => $store.state.loading = false);
    });
</script>